import { useState } from "react";
import { Modal, Form, Input, InputNumber, Select, message } from "antd";
import { useFeeTypes } from "../../../hooks/finance";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";
import { useNavigate } from "react-router-dom";
import useAcademicYears from "../../../hooks/common/useAcademicYears";
import { useEffect } from "react";
import StudentMultiSelect from "../../common/StudentMultiSelect";

const { TextArea } = Input;
const { Option } = Select;

/**
 * Custom Fee Creation Modal
 *
 * Modal for creating custom fee requests for multiple students (bulk only)
 */
const CustomFeeModal = ({
  visible,
  onCancel,
  onSuccess,
  bulkCreateCustomFees,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [selectedAcadyr, setSelectedAcadyr] = useState(null);
  const [selectedClassIds, setSelectedClassIds] = useState([]);

  const navigate = useNavigate();
  const { feeTypes, fetchFeeTypes } = useFeeTypes();
  const { academicYears, fetchAcademicYears } = useAcademicYears();

  // Fetch initial data
  useEffect(() => {
    fetchFeeTypes({ filters: { is_tuition: 0, is_active: 1 } });
    fetchAcademicYears();
    fetchClasses();
  }, []);

  // Fetch students when acadyr or classIds changes
  useEffect(() => {
    if (selectedAcadyr) {
      fetchFilteredStudents();
    } else {
      setStudents([]);
    }
  }, [selectedAcadyr, selectedClassIds]);

  const fetchClasses = async () => {
    setLoadingClasses(true);
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.bootstrap.classes,
        null,
        navigate,
      );
      if (response.ok) {
        const classesData = Array.isArray(response.data) ? response.data : [];
        setClasses(classesData);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
      setClasses([]);
    } finally {
      setLoadingClasses(false);
    }
  };

  const fetchFilteredStudents = async () => {
    setLoadingStudents(true);
    try {
      const params = [];

      if (selectedAcadyr) {
        params.push(`acadyr=${encodeURIComponent(selectedAcadyr)}`);
      }

      if (selectedClassIds && selectedClassIds.length > 0) {
        params.push(`class_ids=${selectedClassIds.join(",")}`);
      }

      params.push("status=ADMITTED");

      const queryString = params.join("&");
      const url = `${domainSettings.urls.enrollment.student}?${queryString}`;

      const response = await Utilities.fetchWithAuth(
        "GET",
        url,
        null,
        navigate,
      );

      if (response.ok) {
        const studentsData = response.data?.results || response.data || [];
        const validStudentsData = Array.isArray(studentsData)
          ? studentsData
          : [];
        setStudents(validStudentsData);

        if (validStudentsData.length === 0) {
          message.info("No students found with the selected criteria");
        }
      } else {
        setStudents([]);
        Utilities.showError(response, "Failed to fetch students");
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      setStudents([]);
      message.error("An error occurred while fetching students");
    } finally {
      setLoadingStudents(false);
    }
  };

  /**
   * Handle bulk custom fees submission
   */
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      console.log("Form values:", values);

      // Format data for API - use exact field names from backend
      const data = {
        studentSpcs: Array.isArray(values.studentSpcs)
          ? values.studentSpcs
          : [],
        feeTypeId: parseInt(values.feeTypeId),
        description: values.description,
        amount: parseFloat(values.amount),
        acadyr: values.acadyr,
        notes: values.notes || "",
      };

      console.log("Sending data:", data);

      setLoading(true);
      const result = await bulkCreateCustomFees(data);

      if (result) {
        form.resetFields();
        setStudents([]);
        setSelectedAcadyr(null);
        setSelectedClassIds([]);
        message.success("Custom fee request created successfully");
        onSuccess();
      }
    } catch (error) {
      console.error("Submission error:", error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle modal close
   */
  const handleCancel = () => {
    form.resetFields();
    setStudents([]);
    setSelectedAcadyr(null);
    setSelectedClassIds([]);
    onCancel();
  };

  return (
    <Modal
      title="Create Custom Fee Request"
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      width={600}
      okText="Create"
      cancelText="Cancel"
    >
      <Form form={form} layout="vertical">
        {/* Fee Type Selection */}
        <Form.Item
          name="feeTypeId"
          label="Fee Type"
          rules={[{ required: true, message: "Please select fee type" }]}
        >
          <Select
            placeholder="Select fee type"
            showSearch
            optionFilterProp="children"
          >
            {Array.isArray(feeTypes) &&
              feeTypes.map((feeType) => (
                <Option key={feeType.feeTypeId} value={feeType.feeTypeId}>
                  {feeType.name} ({feeType.code})
                </Option>
              ))}
          </Select>
        </Form.Item>

        {/* Description */}
        <Form.Item
          name="description"
          label="Description"
          rules={[{ required: true, message: "Please enter description" }]}
        >
          <TextArea
            rows={2}
            placeholder="Describe this custom fee (e.g., Science fair trip to Nairobi)"
          />
        </Form.Item>

        {/* Amount */}
        <Form.Item
          name="amount"
          label="Amount"
          rules={[
            { required: true, message: "Please enter amount" },
            {
              type: "number",
              min: 1,
              message: "Amount must be greater than 0",
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder="0"
            min={0}
            precision={0}
            formatter={(value) => {
              if (!value) return "";
              return `UGX ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            }}
            parser={(value) => {
              // Only parse the number part, ignore UGX text
              const parsed = value.replace(/UGX\s?|(,)/g, "");
              return parsed === "" ? 0 : parseInt(parsed);
            }}
          />
        </Form.Item>

        {/* Academic Year (Required for filtering students) */}
        <Form.Item
          name="acadyr"
          label="Academic Year"
          rules={[{ required: true, message: "Please select academic year" }]}
        >
          <Select
            placeholder="Select academic year"
            onChange={(value) => {
              setSelectedAcadyr(value);
            }}
          >
            {Array.isArray(academicYears) &&
              academicYears.map((year) => (
                <Option key={year.id} value={year.acadyr}>
                  {year.acadyr}
                </Option>
              ))}
          </Select>
        </Form.Item>

        {/* Class (Optional - for filtering students, supports multiple) */}
        <Form.Item name="classIds" label="Class (Optional)">
          <Select
            mode="multiple"
            placeholder="Select one or more classes to filter students"
            loading={loadingClasses}
            allowClear
            onChange={(values) => {
              setSelectedClassIds(values || []);
            }}
          >
            {Array.isArray(classes) &&
              classes.map((cls) => (
                <Option key={cls.id} value={cls.id}>
                  {cls.name}
                </Option>
              ))}
          </Select>
        </Form.Item>

        {/* Student Selection */}
        <Form.Item
          name="studentSpcs"
          label="Select Students"
          rules={[
            { required: true, message: "Please select at least one student" },
          ]}
        >
          <StudentMultiSelect
            students={students}
            placeholder={
              selectedAcadyr
                ? `Found ${students.length} student(s) - Click to select`
                : "Select academic year first to load students"
            }
            loading={loadingStudents}
            disabled={!selectedAcadyr}
          />
        </Form.Item>

        {/* Notes */}
        <Form.Item name="notes" label="Notes (Optional)">
          <TextArea
            rows={2}
            placeholder="Any additional notes about this custom fee"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CustomFeeModal;
